// iOS WKWebView PDF bridge.
//
// WKWebView has no `WKDownloadDelegate` and no Print dialog for blob iframes,
// so jsPDF's `pdf.save()` / `saveAs(blob, …)` / `<a download>` all silently
// no-op on iPhone. The native host (`WebViewerViewController`) registers a
// `printQrPdf` script message handler that decodes a base64 PDF, writes it
// to a temp file, and presents `UIActivityViewController` (Print / Save to
// Files / AirDrop / Share). This helper feature-detects that handler and
// hands the PDF off when present. The name `printQrPdf` is historic — the
// handler is generic, so any web-app PDF export can reuse it.

/**
 * If we're running inside the iOS WKWebView host, hand the jsPDF document off
 * to the native bridge and return `true`. Returns `false` on desktop / other
 * browsers so the caller can fall back to `pdf.save(filename)` or whatever
 * existing path it had.
 *
 * @param {import('jspdf').jsPDF} pdf A jsPDF document.
 * @param {string} filename Desired filename (with `.pdf` suffix).
 * @returns {boolean} `true` if the bridge handled it.
 */
export function dispatchPdfToIosBridgeIfPresent(pdf, filename) {
  const handler = window.webkit?.messageHandlers?.printQrPdf;
  if (!handler) return false;
  const pdfBase64 = bytesToBase64(new Uint8Array(pdf.output('arraybuffer')));
  handler.postMessage({ pdfBase64, filename });
  return true;
}

/**
 * Base64 of a byte array, for handing binary files to a native message
 * handler (postMessage carries strings, not blobs).
 *
 * @param {Uint8Array} bytes
 * @returns {string}
 */
export function bytesToBase64(bytes) {
  // String.fromCharCode.apply chunked to avoid blowing the stack on large PDFs.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length))
    );
  }
  return btoa(binary);
}
